import React, { useEffect, useMemo, useState } from 'react';
import * as echarts from 'echarts';
import {
  useAppStateAssetGoalsListStore,
  useAppStateAssetReportListStore,
  useAppStateManageAssetGoalsModalDataStore,
} from '../../../common/store/AppStateStore.zustand';
import { AssetGoals } from '../type/AssetGoals.type';
import { numberWithCommas, percentageString } from '../../../common/util/NumberUtils';
import AssetGoalsCardLineChart, { AssetGoalsCardLineChartData } from './AssetGoalsCardLineChart';
import { getAssetGoalsCardData } from '../util/AssetGoalsUtils';
import { getNetAssetTotalAmount } from '../../assetReport/util/AssetStatusCard.util';

const goals = [
  { id: 'goal1', name: '주택 구매 자금', target: 12000000, current: 7000000 },
  { id: 'goal2', name: '은퇴 자금', target: 50000000, current: 15000000 },
  {
    id: 'goal3',
    name: '자녀 교육 자금',
    target: 30000000,
    current: 10000000,
  },
];

const AssetGoalsCard = () => {
  const { manageAssetGoalsModalData, setManageAssetGoalsModalData } = useAppStateManageAssetGoalsModalDataStore();
  const { assetGoalsList } = useAppStateAssetGoalsListStore();
  const { assetReportList } = useAppStateAssetReportListStore();
  const [currentAssetGoals, setCurrentAssetGoals] = useState<AssetGoals>();

  useEffect(() => {
    if (assetGoalsList && assetGoalsList.length > 0) {
      setCurrentAssetGoals(assetGoalsList[0]);
    } else {
      setCurrentAssetGoals(undefined);
    }
  }, [assetGoalsList]);

  const assetGoalsCardData = useMemo(() => {
    if (!currentAssetGoals) return;
    return getAssetGoalsCardData(currentAssetGoals, assetReportList);
  }, [currentAssetGoals, assetReportList]);

  const handleAssetGoalsSelect = (assetGoalsId: string) => {
    const selectedAssetGoals = assetGoalsList.find((assetGoals) => assetGoals.id === assetGoalsId);
    if (!selectedAssetGoals) {
      setCurrentAssetGoals(undefined);
    } else {
      setCurrentAssetGoals(selectedAssetGoals);
    }
  };

  const handleAssetGoalsClick = () => {
    setManageAssetGoalsModalData({ isOpen: true, onClose: () => setManageAssetGoalsModalData(undefined) });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-all duration-300 group relative overflow-hidden">
      <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-600/5 rounded-full -translate-y-32 translate-x-32 group-hover:translate-y-[-8rem] transition-transform duration-500"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-600/5 rounded-full translate-y-32 -translate-x-32 group-hover:translate-y-[8rem] transition-transform duration-500"></div>
      <div className="flex justify-between items-center mb-6 relative">
        <div>
          <h3 className="text-xl font-semibold text-gray-800 mb-1 flex items-center">
            <span>목표 달성 현황</span>
          </h3>
          <p className="text-sm text-gray-500">재무 목표 진행 상황</p>
        </div>
        <div>
          <select
            value={currentAssetGoals?.id}
            onChange={(e) => handleAssetGoalsSelect(e.target.value)}
            className="appearance-none bg-white border border-gray-200 rounded-lg py-2 px-4 pr-8 text-gray-700 shadow-sm hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100 focus:border-indigo-500 cursor-pointer !rounded-button whitespace-nowrap transition-all duration-200"
          >
            {assetGoalsList.map((assetGoals) => (
              <option value={assetGoals.id}>{assetGoals.name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
            <i className="fas fa-chevron-down"></i>
          </div>
        </div>
      </div>
      {!assetGoalsCardData && (
        <div className="flex flex-col items-center justify-center h-64 bg-gray-50/50 rounded-lg p-6">
          <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mb-4">
            <i className="fas fa-bullseye text-3xl text-indigo-300"></i>
          </div>
          <h4 className="text-lg font-medium text-gray-700 mb-2">설정된 목표가 없습니다</h4>
          <p className="text-sm text-gray-500 text-center max-w-sm">새로운 목표를 설정하고 달성 현황을 확인해보세요</p>
          <button
            className="mt-6 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-all duration-200 flex items-center space-x-2 !rounded-button whitespace-nowrap"
            onClick={handleAssetGoalsClick}
          >
            <i className="fas fa-plus"></i>
            <span>목표 설정하기</span>
          </button>
        </div>
      )}
      {currentAssetGoals && assetGoalsCardData && (
        <>
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div className="bg-blue-50 text-blue-700 px-4 py-1.5 rounded-full text-sm font-medium">
                {currentAssetGoals.startYearMonth} ~ {currentAssetGoals.endYearMonth}
              </div>
              <div className="flex items-center">
                <div className="w-2 h-2 rounded-full bg-green-500 mr-2"></div>
                <span className="text-sm font-medium">{percentageString(assetGoalsCardData.achievementRate)}</span>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">목표 금액</span>
              <span className="text-sm font-medium">{numberWithCommas(currentAssetGoals.endAmount)} 원</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-600 h-2 rounded-full"
                style={{ width: `${assetGoalsCardData.achievementRate}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-sm text-gray-600">현재 금액</span>
              <span className="text-sm font-medium">{numberWithCommas(assetGoalsCardData.lastNetAssetAmount)} 원</span>
            </div>
          </div>
          {assetGoalsCardData && <AssetGoalsCardLineChart {...assetGoalsCardData} />}
        </>
      )}
    </div>
  );
};

export default AssetGoalsCard;
